import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  IconButton,
  Box,
  Typography
} from '@material-ui/core';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  DeleteOutline as DeleteIcon,
  Check as CheckIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { getTokens } from '../../styles/tokens';

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    recorderContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      minHeight: 56,
      padding: '8px 12px',
      border: `1px dashed ${t.lineStrong}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      marginBottom: theme.spacing(2)
    },
    recordIcon: {
      color: t.semantic.error
    },
    recordingIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0
    },
    recordingDot: {
      flex: 'none',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: t.semantic.error,
      animation: '$recordingPulse 1.2s ease-in-out infinite',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' }
    },
    '@keyframes recordingPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.25 }
    },
    recordingLabel: {
      fontSize: 13,
      color: t.textSecondary
    },
    timeDisplay: {
      fontSize: 15,
      fontWeight: 600,
      fontVariantNumeric: 'tabular-nums',
      color: t.text
    },
    spacer: {
      flex: 1,
      minWidth: 0
    },
    recordedInfo: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      lineHeight: 1.3
    },
    recordedTitle: {
      fontSize: 13.5,
      fontWeight: 600,
      color: t.text
    },
    recordedHint: {
      fontSize: 12,
      color: t.textSecondary
    },
    roundButton: {
      width: 36,
      height: 36,
      padding: 6,
      borderRadius: '50%'
    },
    deleteButton: {
      color: t.semantic.error
    },
    confirmButton: {
      color: t.semantic.success
    }
  };
});

const AudioRecorder = ({ onAudioRecorded, onAudioDeleted, disabled = false }) => {
  const classes = useStyles();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // ✅ CORREÇÃO: Determinar melhor formato baseado no suporte do browser
  const getBestAudioFormat = () => {
    const formats = [
      'audio/ogg; codecs=opus',    // Preferido - WhatsApp compatível
      'audio/webm; codecs=opus',   // Fallback 1
      'audio/mp4',                 // Fallback 2
      'audio/wav'                  // Último recurso
    ];

    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        console.log(`📱 Usando formato de áudio: ${format}`);
        return format;
      }
    }
    
    console.warn('⚠️ Nenhum formato preferido suportado, usando padrão');
    return undefined; // Browser decidirá
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 48000,  // ✅ Taxa recomendada para Opus
          channelCount: 1     // ✅ Mono para WhatsApp
        } 
      });
      
      // ✅ CORREÇÃO: Usar formato compatível com WhatsApp
      const mimeType = getBestAudioFormat();
      const options = mimeType ? { mimeType } : {};
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        // ✅ CORREÇÃO: Criar blob com tipo correto para WhatsApp
        const finalMimeType = mediaRecorderRef.current.mimeType || 'audio/ogg';
        const blob = new Blob(chunksRef.current, { type: finalMimeType });
        
        console.log(`🎵 Áudio gravado: ${blob.size} bytes, tipo: ${finalMimeType}`);
        setAudioBlob(blob);
        
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Parar todas as tracks de áudio
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      
      // Iniciar timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error);
      alert('Erro ao acessar o microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const deleteRecording = () => {
    setAudioBlob(null);
    setRecordingTime(0);
    setIsPlaying(false);
    
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    
    if (onAudioDeleted) {
      onAudioDeleted();
    }
  };

  const confirmRecording = () => {
    if (audioBlob && onAudioRecorded) {
      onAudioRecorded(audioBlob);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box className={classes.recorderContainer}>
      {!audioBlob && !isRecording && (
        <Button
          variant="outlined"
          startIcon={<MicIcon className={classes.recordIcon} />}
          onClick={startRecording}
          disabled={disabled}
        >
          Gravar Áudio
        </Button>
      )}

      {isRecording && (
        <Box className={classes.recordingIndicator}>
          <span className={classes.recordingDot} aria-hidden="true" />
          <Typography component="span" className={classes.recordingLabel}>
            Gravando...
          </Typography>
          <Typography component="span" className={classes.timeDisplay} role="timer">
            {formatTime(recordingTime)}
          </Typography>
          <span className={classes.spacer} />
          <IconButton
            onClick={stopRecording}
            color="primary"
            className={classes.roundButton}
            aria-label="Parar gravação"
          >
            <StopIcon />
          </IconButton>
        </Box>
      )}

      {audioBlob && (
        <>
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={() => setIsPlaying(false)}
            style={{ display: 'none' }}
          />

          <IconButton
            onClick={isPlaying ? pauseAudio : playAudio}
            color="primary"
            className={classes.roundButton}
            aria-label={isPlaying ? 'Pausar' : 'Ouvir gravação'}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>

          <div className={classes.recordedInfo}>
            <span className={classes.recordedTitle}>
              Áudio gravado - {formatTime(recordingTime)}
            </span>
            <span className={classes.recordedHint}>
              Confirme para usar ou exclua para gravar de novo
            </span>
          </div>

          <IconButton
            onClick={deleteRecording}
            className={`${classes.roundButton} ${classes.deleteButton}`}
            aria-label="Excluir gravação"
          >
            <DeleteIcon />
          </IconButton>

          <IconButton
            onClick={confirmRecording}
            className={`${classes.roundButton} ${classes.confirmButton}`}
            aria-label="Confirmar gravação"
          >
            <CheckIcon />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default AudioRecorder;